import { ApiError, api, apiResponse } from './client.ts';
import { apiErrorMessage, statusMessage } from './errors.ts';
import type { components } from './generated/schema.ts';
import type {
  AdminFlag,
  AdminFlagList,
  AccessExplanation,
  AdminGroup,
  AdminGroupDetail,
  AdminUser,
  RoleCatalog,
  RoleDetail,
  UserDirectory,
  AnyPlotResponse,
  AssistantChatRequest,
  AssistantChatResponse,
  AssistantFeedbackRequest,
  AssistantPlotApplyClickedRequest,
  AssistantStatus,
  AuditEvent,
  AuditFilters,
  AuthenticatedUser,
  CatalogChart,
  ChangeoversResponse,
  AssistantConversation,
  AssistantConversationDetail,
  ChartGrant,
  DashboardGrant,
  ColumnInfo,
  DashboardFolderRow,
  DashboardListResponse,
  DashboardRow,
  EstimateResponse,
  ExportJobStarted,
  ExportRequest,
  FilterGroup,
  FlagAudience,
  Info,
  JobInfo,
  LegsResponse,
  PlotRequest,
  PlotResponse,
  PreferencesPayload,
  PreviewResponse,
  ArgusEntity,
  ArgusMark,
  ArgusRule,
  ArgusRuleChart,
  ArgusSchedule,
  ArgusPreview,
  ArgusRuleDocument,
  ArgusScopeSummary,
  ArgusSeries,
  ArgusTreeDocument,
  ArgusFindings,
  ArgusWatcherInfo,
  ArgusInbox,
  ArgusVerdictKey,
  ArgusHealth,
  ArgusHealthFinished,
  ArgusHealthStats,
  ArgusHistory,
  ArgusAttention,
  ArgusBacktest,
  ArgusEpisodeDetail,
  ArgusBacktestSummary,
  ArgusTreeNode,
  ArgusTreeScope,
  ArgusSource,
  ArgusTeams,
  ArgusFolder,
  ArgusNote,
  PrincipalSearch,
  SfocCurves,
  ShareLink,
  ShareResolved,
  ShipDateRange,
  DataSourceInfo,
  SPCorrections,
  SPDistributions,
  SPExplain,
  SPInit,
  SPPredict,
  SPSchema,
  SPSweep,
  SPTimeseries,
  SPVersions,
  NotificationInbox,
  Announcement,
  AnnouncementInput,
  AnnouncementReceipt,
  LiveAnnouncements,
  Transcript,
  TranscriptChat,
  TranscriptOwner,
  TranscriptPerson,
  UsageStats,
} from './types.ts';

export const getCurrentUser = (): Promise<AuthenticatedUser> =>
  api<AuthenticatedUser>('/me');

export const getInfo = (): Promise<Info> => api<Info>('/info');

export const listAdminUsers = (filters: { q?: string; active?: boolean; limit: number; offset: number }): Promise<UserDirectory> => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  return api<UserDirectory>(`/admin/users?${params}`);
};

/** Live banners and pop-ups for the signed-in person. Empty without a database. */
export const getLiveAnnouncements = (): Promise<LiveAnnouncements> =>
  api<LiveAnnouncements>('/announcements');

export const getReleaseNotes = (): Promise<{ available: boolean; notes: Announcement[] }> =>
  api('/announcements/release-notes');

export const sendAnnouncementReceipt = (
  id: string,
  receipt: { popup_seen?: boolean; banner_dismissed?: boolean },
): Promise<void> =>
  api<void>(`/announcements/${encodeURIComponent(id)}/receipt`, {
    method: 'POST', body: JSON.stringify(receipt),
  });

/** `announcements:write`: every announcement that has not been deleted. */
export const listAdminAnnouncements = (): Promise<{ announcements: Announcement[] }> =>
  api('/admin/announcements');

export const createAnnouncement = (input: AnnouncementInput): Promise<Announcement> =>
  api<Announcement>('/admin/announcements', { method: 'POST', body: JSON.stringify(input) });

export const updateAnnouncement = (
  id: string,
  input: AnnouncementInput,
): Promise<Announcement> =>
  api<Announcement>(`/admin/announcements/${encodeURIComponent(id)}`, {
    method: 'PUT', body: JSON.stringify(input),
  });

export const setAnnouncementActive = (id: string, isActive: boolean): Promise<Announcement> =>
  api<Announcement>(`/admin/announcements/${encodeURIComponent(id)}/active`, {
    method: 'PUT', body: JSON.stringify({ is_active: isActive }),
  });

/** Forget who closed or dismissed it: everyone sees it again. Acts at once. */
/** `announcements:write`: who closed the pop-up or dismissed the banner, latest first. */
export const listAnnouncementReceipts = (id: string): Promise<{ receipts: AnnouncementReceipt[] }> =>
  api(`/admin/announcements/${encodeURIComponent(id)}/receipts`);

export const showAnnouncementAgain = (id: string): Promise<{ id: string; cleared: number }> =>
  api(`/admin/announcements/${encodeURIComponent(id)}/show-again`, { method: 'POST' });

export const deleteAnnouncement = (id: string): Promise<void> =>
  api<void>(`/admin/announcements/${encodeURIComponent(id)}`, { method: 'DELETE' });

/** Root only: everyone who has assistant chats. 403 for anyone else. */
export const listTranscriptPeople = (q = ''): Promise<{ people: TranscriptPerson[] }> =>
  api(`/admin/transcripts/people${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''}`);

/** Root only: one person's chats, deleted ones included. */
export const listTranscriptChats = (
  userId: string,
  q = '',
  flagged = false,
): Promise<{ owner: TranscriptOwner; chats: TranscriptChat[] }> => {
  const params = new URLSearchParams();
  if (q.trim()) params.set('q', q.trim());
  if (flagged) params.set('flagged', 'true');
  const query = params.toString();
  return api(`/admin/transcripts/people/${encodeURIComponent(userId)}/chats${query ? `?${query}` : ''}`);
};

/** Root only: questions and dislikes per day and per person. `days` 0 is everything. */
export const getUsageStats = (days: number): Promise<UsageStats> =>
  api<UsageStats>(`/admin/transcripts/stats?days=${days}`);

/** Root only: one chat in full. The server records every call in Activity. */
export const getTranscript = (id: string): Promise<Transcript> =>
  api<Transcript>(`/admin/transcripts/chats/${encodeURIComponent(id)}`);

export const setUserRole = (id: string, role: string, enabled: boolean): Promise<AdminUser> =>
  api<AdminUser>(`/admin/users/${encodeURIComponent(id)}/roles/${encodeURIComponent(role)}`, {
    method: 'PUT', body: JSON.stringify({ enabled }),
  });

export const getAdminUser = (id: string): Promise<AdminUser> =>
  api<AdminUser>(`/admin/users/${encodeURIComponent(id)}`);

/** Replace the one directly assigned Explorer role; null removes it. Group roles still apply. */
export const setUserBaseRole = (id: string, role: string | null): Promise<AdminUser> =>
  api<AdminUser>(`/admin/users/${encodeURIComponent(id)}/base-role`, {
    method: 'PUT', body: JSON.stringify({ role }),
  });

export const setUserRoot = (id: string, enabled: boolean): Promise<AdminUser> =>
  api<AdminUser>(`/admin/users/${encodeURIComponent(id)}/root`, {
    method: 'PUT', body: JSON.stringify({ enabled }),
  });

export const setUserActive = (id: string, enabled: boolean, transferTo?: string, reason?: string): Promise<AdminUser> =>
  api<AdminUser>(`/admin/users/${encodeURIComponent(id)}/active`, {
    method: 'PUT',
    body: JSON.stringify({
      enabled,
      ...(transferTo ? { transfer_to_user_id: transferTo } : {}),
      ...(reason?.trim() ? { reason: reason.trim() } : {}),
    }),
  });

export const listAdminGroups = (q = ''): Promise<{ groups: AdminGroup[] }> =>
  api(`/admin/groups${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''}`);

export const getAdminGroup = (id: string): Promise<AdminGroupDetail> =>
  api<AdminGroupDetail>(`/admin/groups/${encodeURIComponent(id)}`);

export const createAdminGroup = (name: string, description: string | null): Promise<AdminGroupDetail> =>
  api<AdminGroupDetail>('/admin/groups', { method: 'POST', body: JSON.stringify({ name, description }) });

export const updateAdminGroup = (
  id: string,
  patch: { name?: string; description?: string | null },
): Promise<AdminGroupDetail> =>
  api<AdminGroupDetail>(`/admin/groups/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) });

export const deleteAdminGroup = (id: string): Promise<void> =>
  api<void>(`/admin/groups/${encodeURIComponent(id)}`, { method: 'DELETE' });

/** Active people matching `q` who are not in the group yet. */
export const searchGroupCandidates = (
  id: string,
  q: string,
): Promise<Array<{ id: string; display_name: string; email: string | null }>> =>
  api(`/admin/groups/${encodeURIComponent(id)}/candidates?q=${encodeURIComponent(q)}`);

/** Add a member, or change whether they lead. Idempotent. */
export const setGroupMember = (id: string, userId: string, leader: boolean): Promise<AdminGroupDetail> =>
  api<AdminGroupDetail>(`/admin/groups/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`, {
    method: 'PUT', body: JSON.stringify({ leader }),
  });

/** Assign or remove a whole-app role for every member of the group. */
export const setGroupRole = (id: string, role: string, enabled: boolean): Promise<AdminGroupDetail> =>
  api<AdminGroupDetail>(`/admin/groups/${encodeURIComponent(id)}/roles/${encodeURIComponent(role)}`, {
    method: 'PUT', body: JSON.stringify({ enabled }),
  });

/** The predefined roles, the capability matrix and the new-user default. */
export const getRoleCatalog = (): Promise<RoleCatalog> => api<RoleCatalog>('/admin/roles');

export const getRoleDetail = (name: string): Promise<RoleDetail> =>
  api<RoleDetail>(`/admin/roles/${encodeURIComponent(name)}`);

/** The base role future users start with. Existing people keep theirs. */
export const setDefaultRole = (role: string): Promise<RoleCatalog> =>
  api<RoleCatalog>('/admin/access/default', { method: 'PUT', body: JSON.stringify({ role }) });

/** Every capability for one person, allowed or blocked, and why. */
export const getAccessExplanation = (userId: string): Promise<AccessExplanation> =>
  api<AccessExplanation>(`/admin/access/users/${encodeURIComponent(userId)}`);

export const removeGroupMember = (id: string, userId: string): Promise<AdminGroupDetail> =>
  api<AdminGroupDetail>(`/admin/groups/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  });

/** Merge a partial preference change; the reply is everything, resolved. */
export const patchPreferences = (
  patch: {
    theme?: 'light' | 'dark' | null;
    chart?: Record<string, unknown>;
    /** Null takes the lens off, and `lens_set` is what makes null mean that. */
    lens?: string | null;
    lens_set?: boolean;
  },
): Promise<PreferencesPayload> =>
  api<PreferencesPayload>('/me/preferences', { method: 'PATCH', body: JSON.stringify(patch) });

/** Forget this account's preferences, leaving the organisation's defaults. */
export const resetPreferences = (): Promise<PreferencesPayload> =>
  api<PreferencesPayload>('/me/preferences', { method: 'DELETE' });

/** One tour's state on the account. `welcome` is the pop-up's own row. */
export interface TourProgressRow {
  state: string;
  step: number | null;
  step_count: number | null;
  version: number | null;
  updated_at: string | null;
  /** The first time this tour was finished. Kept when it is started again. */
  finished_at?: string | null;
}

/** Guided tours: how far this person got. `stored: false` means no database. */
export const getTourProgress = (): Promise<{ stored: boolean; progress: Record<string, TourProgressRow> }> =>
  api('/me/tours');

/** May this tab show the welcome pop-up? Only one tab ever hears yes. */
export const claimTourWelcome = (): Promise<{ stored: boolean; offer: boolean }> =>
  api('/me/tours/welcome/claim', { method: 'POST' });

export const answerTourWelcome = (answer: 'accepted' | 'later' | 'declined'): Promise<{ stored: boolean }> =>
  api('/me/tours/welcome', { method: 'POST', body: JSON.stringify({ answer }) });

export const putTourProgress = (
  tourId: string,
  update: { state: 'started' | 'finished' | 'stopped'; step: number; step_count: number; version: number },
): Promise<{ stored: boolean }> =>
  api(`/me/tours/${encodeURIComponent(tourId)}`, { method: 'PUT', body: JSON.stringify(update) });

/** Every data source, available or not. */
export const listSources = (): Promise<DataSourceInfo[]> => api<DataSourceInfo[]>('/sources');

/** `?source=` for the read-only ship endpoints; nothing for EOS, the default. */
const sourceQuery = (source?: string, sep: '?' | '&' = '?'): string =>
  source && source !== 'historical' ? `${sep}source=${encodeURIComponent(source)}` : '';

export const listShips = (source?: string): Promise<string[]> =>
  api<string[]>(`/ships${sourceQuery(source)}`);

/** ship_id → public name + brand, for display only. */
export const listShipNames = (): Promise<Record<string, { name: string; brand: string }>> =>
  api<Record<string, { name: string; brand: string }>>('/ships/names');

export const getColumns = (shipId: string, source?: string): Promise<ColumnInfo[]> =>
  api<ColumnInfo[]>(`/ships/${encodeURIComponent(shipId)}/columns${sourceQuery(source)}`);

export const getDateRange = (shipId: string, source?: string): Promise<ShipDateRange> =>
  api<ShipDateRange>(`/ships/${encodeURIComponent(shipId)}/date-range${sourceQuery(source)}`);

export const getColumnValues = (
  shipId: string,
  column: string,
  limit = 50,
  source?: string,
  /** Only the values read in this window. */
  window?: { start: string; end: string },
): Promise<Array<string | number>> =>
  api<Array<string | number>>(
    `/ships/${encodeURIComponent(shipId)}/columns/${encodeURIComponent(column)}/values?limit=${limit}${sourceQuery(source, '&')}` +
      (window
        ? `&start_date=${encodeURIComponent(window.start)}&end_date=${encodeURIComponent(window.end)}`
        : ''),
  );

export interface ColumnProfile {
  column: string;
  /** Time columns are measured in epoch seconds. */
  temporal: boolean;
  min: number | null;
  max: number | null;
  counts: number[];
}

/** Extent and histogram of one column over some ships and a window. */
export const getColumnProfile = (
  shipIds: string[],
  column: string,
  start: string,
  end: string,
  bins = 48,
): Promise<ColumnProfile> =>
  api<ColumnProfile>('/columns/profile', {
    method: 'POST',
    body: JSON.stringify({ ship_ids: shipIds, column, start_date: start, end_date: end, bins }),
  });

export const estimate = (req: ExportRequest): Promise<EstimateResponse> =>
  api<EstimateResponse>('/estimate', {
    method: 'POST',
    body: JSON.stringify(req),
  });

export const preview = (req: ExportRequest): Promise<PreviewResponse> =>
  api<PreviewResponse>('/preview', {
    method: 'POST',
    body: JSON.stringify(req),
  });

export const startExport = (req: ExportRequest): Promise<ExportJobStarted> =>
  api<ExportJobStarted>('/export', {
    method: 'POST',
    body: JSON.stringify(req),
  });

export const getJob = (jobId: string): Promise<JobInfo> =>
  api<JobInfo>(`/jobs/${encodeURIComponent(jobId)}`);

export const getLegs = (req: {
  ship_id: string;
  start_date: string;
  end_date: string;
  filters?: FilterGroup | null;
}): Promise<LegsResponse> =>
  api<LegsResponse>('/legs', {
    method: 'POST',
    body: JSON.stringify(req),
  });

export const getAllLegs = (shipId: string): Promise<LegsResponse> =>
  api<LegsResponse>('/legs/all', {
    method: 'POST',
    body: JSON.stringify({ ship_id: shipId }),
  });

export const getChangeovers = (req: {
  ship_id: string;
  start_date: string;
  end_date: string;
  filters?: FilterGroup | null;
}): Promise<ChangeoversResponse> =>
  api<ChangeoversResponse>('/changeovers', {
    method: 'POST',
    body: JSON.stringify(req),
  });

/** `refresh` marks an automatic Live re-fetch, which the usage log skips. */
export const runPlot = (req: PlotRequest, refresh = false): Promise<AnyPlotResponse> =>
  api<AnyPlotResponse>('/plot', {
    method: 'POST',
    body: JSON.stringify(req),
    ...(refresh ? { headers: { 'X-Plot-Refresh': '1' } } : {}),
  });

/** A page was opened. The server records it at most once in five minutes. */
export const recordVisit = (page: 'live-status'): Promise<void> =>
  api<void>('/usage/visit', { method: 'POST', body: JSON.stringify({ page }) });

/* ─── Assistant ───────────────────────────────────── */

export const getNotifications = (): Promise<NotificationInbox> =>
  api<NotificationInbox>('/notifications');

/** Omit `ids` to mark everything read, which is what "Mark all read" sends. */
export const markNotificationsRead = (ids?: string[]): Promise<void> =>
  api<void>('/notifications/read', {
    method: 'POST',
    body: JSON.stringify({ ids: ids ?? [] }),
  });

export const getAssistantStatus = (): Promise<AssistantStatus> =>
  api<AssistantStatus>('/assistant/status');

/* ── the assistant's stored chats ───────────────────────────────────────────
   `ai.conversation` / `ai.turn`. The list carries no transcripts: it is a rail
   of names and totals, and a chat's turns arrive when it is opened. */

export const listAssistantConversations = (
  query?: string,
): Promise<{ conversations: AssistantConversation[] }> =>
  api(`/assistant/conversations${query ? `?q=${encodeURIComponent(query)}` : ''}`);

export const getAssistantConversation = (id: string): Promise<AssistantConversationDetail> =>
  api<AssistantConversationDetail>(`/assistant/conversations/${encodeURIComponent(id)}`);

export const deleteAssistantConversation = (id: string): Promise<void> =>
  api<void>(`/assistant/conversations/${encodeURIComponent(id)}`, { method: 'DELETE' });

/** Hand this browser's chats to the account. Once per person. */
export const importAssistantConversations = (
  chats: unknown[],
): Promise<{ imported: number }> =>
  api('/assistant/conversations/import', {
    method: 'POST',
    body: JSON.stringify({ chats }),
  });

/** One line of `/assistant/chat/stream`. */
type AssistantChatEvent =
  | { type: 'step'; text: string }
  | { type: 'done'; response: AssistantChatResponse }
  | { type: 'error'; status: number; detail: string | null };

/**
 * Read a turn as it streams: `onStep` hears each thing the assistant starts
 * ("Looking for the right signals"), and the promise settles with the reply -
 * or with the error `/assistant/chat` would have answered.
 */
export async function readAssistantChatStream(
  resp: Response,
  onStep?: (text: string) => void,
): Promise<AssistantChatResponse> {
  const ref = resp.headers.get('X-Request-Id');
  const reader = resp.body?.getReader();
  const decoder = new TextDecoder();
  let buffered = '';
  const handle = (line: string): AssistantChatResponse | null => {
    if (!line.trim()) return null;
    const event = JSON.parse(line) as AssistantChatEvent;
    if (event.type === 'step') onStep?.(event.text);
    else if (event.type === 'done') return event.response;
    else if (event.type === 'error') {
      throw new ApiError(apiErrorMessage(event.status, event.detail, ref), event.status, {
        ref,
        serverDetail: event.detail,
      });
    }
    return null;
  };
  while (reader) {
    const { done, value } = await reader.read();
    buffered += decoder.decode(value, { stream: !done });
    const lines = buffered.split('\n');
    buffered = lines.pop() ?? '';
    for (const line of lines) {
      const response = handle(line);
      if (response) return response;
    }
    if (done) break;
  }
  const response = handle(buffered);
  if (response) return response;
  // The connection closed before the answer: a proxy or a restart in between.
  throw new ApiError(statusMessage(502), 502, { ref });
}

export async function runAssistantChat(
  req: AssistantChatRequest,
  onStep?: (text: string) => void,
): Promise<AssistantChatResponse> {
  // The backend may make several LLM round-trips per turn; give it room. The
  // clock covers reading the stream too, not only its first byte.
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 120000);
  try {
    const resp = await apiResponse('/assistant/chat/stream', {
      method: 'POST',
      body: JSON.stringify(req),
      signal: ctrl.signal,
      timeoutMs: 120000,
    });
    return await readAssistantChatStream(resp, onStep);
  } catch (error) {
    // Our own clock ran out: a slow answer, not a missing server.
    if (ctrl.signal.aborted) {
      throw new ApiError(statusMessage(408), 0);
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

export const sendAssistantFeedback = (
  req: AssistantFeedbackRequest,
): Promise<{ recorded: boolean }> =>
  api<{ recorded: boolean }>('/assistant/feedback', {
    method: 'POST',
    body: JSON.stringify(req),
  });

export const recordAssistantPlotApplyClicked = (
  req: AssistantPlotApplyClickedRequest,
): Promise<{ recorded: boolean }> =>
  api<{ recorded: boolean }>('/assistant/plot-apply-clicked', {
    method: 'POST',
    body: JSON.stringify(req),
  });

/* ─── SP model viewer ──────────────────────────────────── */

export const getSpVersions = (shipId: string): Promise<SPVersions> =>
  api<SPVersions>(`/sp-model/versions?ship_id=${encodeURIComponent(shipId)}`);

export const getSpSchema = (shipId: string, version: string): Promise<SPSchema> =>
  api<SPSchema>(
    `/sp-model/schema?ship_id=${encodeURIComponent(shipId)}&version=${encodeURIComponent(version)}`,
  );

export const runSpDistributions = (req: {
  ship_id: string;
  version: string;
  start_date: string;
  end_date: string;
  bins?: number;
}): Promise<SPDistributions> =>
  api<SPDistributions>('/sp-model/distributions', {
    method: 'POST',
    body: JSON.stringify(req),
  });

export const spInit = (req: {
  ship_id: string;
  version: string;
  /** Omit/null to load the latest available row. */
  datetime?: string | null;
}): Promise<SPInit> =>
  api<SPInit>('/sp-model/init', {
    method: 'POST',
    body: JSON.stringify(req),
  });

export const spPredict = (req: {
  ship_id: string;
  version: string;
  features: Record<string, number>;
}): Promise<SPPredict> =>
  api<SPPredict>('/sp-model/predict', {
    method: 'POST',
    body: JSON.stringify(req),
  });

export const spExplain = (req: {
  ship_id: string;
  version: string;
  features: Record<string, number>;
}): Promise<SPExplain> =>
  api<SPExplain>('/sp-model/explain', {
    method: 'POST',
    body: JSON.stringify(req),
  });

export const spTimeseries = (req: {
  ship_id: string;
  version: string;
  start_date: string;
  end_date: string;
  max_points?: number;
}): Promise<SPTimeseries> =>
  api<SPTimeseries>('/sp-model/timeseries', {
    method: 'POST',
    body: JSON.stringify(req),
  });

export const spCorrections = (req: {
  ship_id: string;
  start_date: string;
  end_date: string;
}): Promise<SPCorrections> =>
  api<SPCorrections>('/sp-model/corrections', {
    method: 'POST',
    body: JSON.stringify(req),
  });

export const runSpSweep = (req: {
  ship_id: string;
  version: string;
  x_feature: string;
  features: Record<string, number>;
  x_min: number;
  x_max: number;
  points?: number;
  tolerances: Record<string, number>;
  start_date: string;
  end_date: string;
}): Promise<SPSweep> =>
  api<SPSweep>('/sp-model/sweep', {
    method: 'POST',
    body: JSON.stringify(req),
  });

/** SFOC curves for a ship (per engine, or generic). `date` is YYYY-MM-DD (UTC);
 * omit to use the backend's default of today. */
export const getSfocCurves = (shipId: string, date?: string): Promise<SfocCurves> => {
  // Always with history: the viewer draws the version in force at whatever
  // moment its time slicer is on, so it needs every version, not just the last.
  const params = new URLSearchParams({ ship_id: shipId, history: 'true' });
  if (date) params.set('date', date);
  return api<SfocCurves>(`/sfoc/curves?${params.toString()}`);
};

/* ─── Catalog and sharing ──────────────────────────────── */

/**
 * Shorten the current view into a link.
 *
 * Content-addressed on the server, so sharing the same view twice returns the
 * same id rather than a second row - `created` says which happened.
 * `pin_context` keeps the ship and date range in the link; without it the link
 * carries the recipe only, and opens against whatever the recipient has open.
 */
export const createShareLink = (
  query: string,
  opts: { pinContext?: boolean; title?: string; kind?: ShareResolved['kind'] } = {},
): Promise<ShareLink> =>
  api<ShareLink>('/share', {
    method: 'POST',
    body: JSON.stringify({
      query,
      pin_context: opts.pinContext ?? true,
      title: opts.title ?? null,
      kind: opts.kind ?? 'explore',
    }),
  });

/** Expand `/s/<id>` back into the query string the app restores from. */
export const resolveShareLink = (id: string): Promise<ShareResolved> =>
  api<ShareResolved>(`/share/${encodeURIComponent(id)}`);

/** The catalog's tabs: `mine`, `shared` (with me), `public`, or all three. */
export const listCharts = (
  scope: 'all' | 'mine' | 'shared' | 'public' = 'all',
  search?: string,
): Promise<CatalogChart[]> => {
  const params = new URLSearchParams({ scope });
  if (search) params.set('q', search);
  return api<CatalogChart[]>(`/charts?${params.toString()}`);
};

export const getChart = (id: string): Promise<CatalogChart> =>
  api<CatalogChart>(`/charts/${encodeURIComponent(id)}`);

export const createChart = (body: {
  title: string;
  query: string;
  description?: string | null;
  bind_context?: boolean;
  pin_ship?: boolean;
  pin_time?: boolean;
  tags?: string[];
  folder_id?: string | null;
}): Promise<CatalogChart> =>
  api<CatalogChart>('/charts', { method: 'POST', body: JSON.stringify(body) });

/**
 * Patch a chart. Pass `version` to be told about a concurrent edit (409) rather
 * than silently overwriting one.
 */
export const updateChart = (
  id: string,
  body: Partial<{
    title: string;
    query: string;
    description: string | null;
    bind_context: boolean;
    pin_ship: boolean;
    pin_time: boolean;
    tags: string[];
    folder_id: string | null;
    version: number;
  }>,
): Promise<CatalogChart> =>
  api<CatalogChart>(`/charts/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

export const deleteChart = (id: string): Promise<void> =>
  api<void>(`/charts/${encodeURIComponent(id)}`, { method: 'DELETE' });

/* ── dashboards ─────────────────────────────────────────────────────────────
   The list is deliberately documentless: `spec` arrives only for the dashboard
   being opened. See `docs/plans/dashboards.md`, "Why the document stays one JSONB column". */

export const listDashboards = (): Promise<DashboardListResponse> =>
  api<DashboardListResponse>('/dashboards');

export const getDashboard = (id: string): Promise<DashboardRow> =>
  api<DashboardRow>(`/dashboards/${encodeURIComponent(id)}`);

export const createDashboard = (body: {
  title: string;
  spec: unknown;
  folder_id?: string | null;
  description?: string | null;
  tags?: string[];
}): Promise<DashboardRow> =>
  api<DashboardRow>('/dashboards', { method: 'POST', body: JSON.stringify(body) });

/**
 * Save a dashboard. `version` is what was last read; a 409 means somebody saved
 * first and carries their version, rather than this write winning by arriving
 * second.
 */
export const saveDashboard = (
  id: string,
  body: {
    version?: number;
    title?: string;
    spec?: unknown;
    folder_id?: string | null;
    move_to_root?: boolean;
    description?: string | null;
    tags?: string[];
  },
): Promise<DashboardRow> =>
  api<DashboardRow>(`/dashboards/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

export const deleteDashboard = (id: string): Promise<void> =>
  api<void>(`/dashboards/${encodeURIComponent(id)}`, { method: 'DELETE' });

/** Hand this browser's stored dashboards to the account. Once per person. */
export const importDashboards = (body: {
  dashboards: Array<{ title: string; spec: unknown; folder_id?: string | null }>;
  folders: Array<{ name: string }>;
}): Promise<{ dashboards: DashboardRow[]; failed: number }> =>
  api('/dashboards/import', { method: 'POST', body: JSON.stringify(body) });

export const createDashboardFolder = (name: string): Promise<DashboardFolderRow> =>
  api<DashboardFolderRow>('/dashboard-folders', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });

export const renameDashboardFolder = (id: string, name: string): Promise<DashboardFolderRow> =>
  api<DashboardFolderRow>(`/dashboard-folders/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify({ name }),
  });

export const deleteDashboardFolder = (id: string): Promise<void> =>
  api<void>(`/dashboard-folders/${encodeURIComponent(id)}`, { method: 'DELETE' });

/* ── sharing a dashboard ────────────────────────────────────────────────────
   The catalog's three routes, over `iam.role_binding` rows scoped to a
   dashboard. No `everyone`: a dashboard has no public shelf to put it on. */

export const listDashboardGrants = (id: string): Promise<DashboardGrant[]> =>
  api<DashboardGrant[]>(`/dashboards/${encodeURIComponent(id)}/grants`);

export const grantDashboard = (
  id: string,
  body: {
    role: 'viewer' | 'editor';
    user_id?: string;
    group_id?: string;
    expires_at?: string | null;
  },
): Promise<{ id: string; granted_at: string; expires_at: string | null }> =>
  api(`/dashboards/${encodeURIComponent(id)}/grants`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const changeDashboardGrant = (
  id: string,
  grantId: string,
  role: 'viewer' | 'editor',
): Promise<{ id: string }> =>
  api(`/dashboards/${encodeURIComponent(id)}/grants/${encodeURIComponent(grantId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  });

export const revokeDashboardGrant = (id: string, grantId: string): Promise<void> =>
  api<void>(`/dashboards/${encodeURIComponent(id)}/grants/${encodeURIComponent(grantId)}`, {
    method: 'DELETE',
  });

/** People and groups for the dashboard share dialog - see the route's own note. */
export const searchDashboardPrincipals = (q: string): Promise<PrincipalSearch> =>
  api<PrincipalSearch>(`/dashboard-principals?q=${encodeURIComponent(q)}`);

export const listChartGrants = (id: string): Promise<ChartGrant[]> =>
  api<ChartGrant[]>(`/charts/${encodeURIComponent(id)}/grants`);

/** Share one chart with a person, a group, or everyone signed in. */
export const grantChart = (
  id: string,
  body: {
    role?: 'viewer' | 'editor';
    user_id?: string;
    group_id?: string;
    everyone?: boolean;
    expires_at?: string | null;
  },
): Promise<{ id: string; granted_at: string; expires_at: string | null }> =>
  api(`/charts/${encodeURIComponent(id)}/grants`, {
    method: 'POST',
    body: JSON.stringify({ role: 'viewer', ...body }),
  });

/** Make an existing grant `viewer` or `editor`; the person and expiry carry over. */
export const changeChartGrant = (
  id: string,
  grantId: string,
  role: 'viewer' | 'editor',
): Promise<{ id: string }> =>
  api(`/charts/${encodeURIComponent(id)}/grants/${encodeURIComponent(grantId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  });

/** Put a chart in the Public catalog (view-only for everyone), or take it out. */
export const setChartPublic = (id: string, isPublic: boolean): Promise<{ is_public: boolean }> =>
  api(`/charts/${encodeURIComponent(id)}/public`, {
    method: 'PUT',
    body: JSON.stringify({ public: isPublic }),
  });

export const revokeChartGrant = (id: string, grantId: string): Promise<void> =>
  api<void>(`/charts/${encodeURIComponent(id)}/grants/${encodeURIComponent(grantId)}`, {
    method: 'DELETE',
  });

/** People and groups to offer in the share dialog. */
export const searchPrincipals = (q: string): Promise<PrincipalSearch> =>
  api<PrincipalSearch>(`/principals?q=${encodeURIComponent(q)}`);

/** Store what a chart looks like, captured from the browser canvas. */
export const putChartThumbnail = (
  id: string,
  body: { data_url: string; width: number; height: number; version: number },
): Promise<void> =>
  api<void>(`/charts/${encodeURIComponent(id)}/thumbnail`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

/**
 * Fetch a chart's preview as an object URL, or null when it has none.
 *
 * Goes through `apiResponse` rather than an `<img src>` because the endpoint
 * needs the bearer token like everything else; the blob is what an `<img>` can
 * then point at. Callers cache the result - see `features/catalog/thumbnail.ts`.
 */
export const fetchChartThumbnail = async (id: string): Promise<string | null> => {
  try {
    const resp = await apiResponse(`/charts/${encodeURIComponent(id)}/thumbnail`);
    return URL.createObjectURL(await resp.blob());
  } catch {
    return null;
  }
};

/* ─── Activity log ─────────────────────────────────────── */

/** The activity feed, newest first. Requires `audit:read`; 403 otherwise. */
export const listAuditEvents = (filters: AuditFilters = {}): Promise<AuditEvent[]> => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const query = params.toString();
  return api<AuditEvent[]>(`/audit/events${query ? `?${query}` : ''}`);
};

/** The actions actually present in the log, for the filter dropdown. */
export const listAuditActions = (): Promise<string[]> => api<string[]>('/audit/actions');

export type ActivityStats = components['schemas']['ActivityStats'];
export type ActivityPlot = components['schemas']['ActivityPlot'];

/** Activity → Statistics: plots, exports and page visits in numbers. `days` 0 is everything. */
export const getActivityStats = (days: number): Promise<ActivityStats> =>
  api<ActivityStats>(`/audit/usage/stats?days=${days}`);

/** One person's plots and exports, newest first. */
export const listPersonPlots = (userId: string, days: number, offset = 0): Promise<ActivityPlot[]> =>
  api<ActivityPlot[]>(`/audit/usage/people/${encodeURIComponent(userId)}/plots?days=${days}&limit=100&offset=${offset}`);

// ── feature flags (admin) ───────────────────────────────────────────────────

/** Every flag, its rules, and the audience those rules describe. */
export const listAdminFlags = (): Promise<AdminFlagList> => api<AdminFlagList>('/admin/flags');

/** People and groups for the audience picker - includes yourself, unlike sharing. */
export const searchFlagPrincipals = (q: string): Promise<PrincipalSearch> =>
  api<PrincipalSearch>(`/admin/flags/principals?q=${encodeURIComponent(q)}`);

/** Set who gets a feature. Replaces the flag's rules with the canonical set. */
export const setFlagAudience = (
  key: string,
  body: {
    audience: Exclude<FlagAudience, 'custom'>;
    principals: { kind: 'user' | 'group'; id: string }[];
    exceptions: { kind: 'user' | 'group'; id: string }[];
  },
): Promise<AdminFlag> =>
  api<AdminFlag>(`/admin/flags/${encodeURIComponent(key)}/audience`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

/** The kill switch: off means every rule is ignored and the default applies. */
export const setFlagActive = (key: string, isActive: boolean): Promise<AdminFlag> =>
  api<AdminFlag>(`/admin/flags/${encodeURIComponent(key)}/active`, {
    method: 'PUT',
    body: JSON.stringify({ is_active: isActive }),
  });

/** Remove one rule, including a hand-written one the picker cannot express. */
export const deleteFlagRule = (key: string, ruleId: string): Promise<AdminFlag> =>
  api<AdminFlag>(
    `/admin/flags/${encodeURIComponent(key)}/rules/${encodeURIComponent(ruleId)}`,
    { method: 'DELETE' },
  );

/* ─── Argus ─────────────────────────────────────────────────
   Every one of these is refused unless the `argus` flag is on for this
   account, and the writes again unless it holds `argus:author`. See docs/plans/argus.md. */

/** What may be watched, and what each source can promise. */
export const listArgusSources = (): Promise<{ sources: ArgusSource[] }> =>
  api<{ sources: ArgusSource[] }>('/argus/sources');

/** The things a source counts - vessels here, something else elsewhere. */
export const listArgusEntities = (
  sourceKey: string,
): Promise<{ entity_noun: string; entities: ArgusEntity[] }> =>
  api<{ entity_noun: string; entities: ArgusEntity[] }>(
    `/argus/sources/${encodeURIComponent(sourceKey)}/entities`,
  );

/** Only what a rule can watch: a rule watches numbers. */
export const listArgusSeries = (
  sourceKey: string,
  entityId: string,
): Promise<{ series: ArgusSeries[] }> =>
  api<{ series: ArgusSeries[] }>(
    `/argus/sources/${encodeURIComponent(sourceKey)}/entities/${encodeURIComponent(entityId)}/series`,
  );

/** Whom a scope reaches today, and what they carry - for the block editor. */
export const resolveArgusScope = (
  sourceKey: string,
  scope: ArgusTreeScope,
): Promise<ArgusScopeSummary> =>
  api<ArgusScopeSummary>(`/argus/sources/${encodeURIComponent(sourceKey)}/scope`, {
    method: 'POST',
    body: JSON.stringify({ scope }),
  });

/** The server's sentence for a rule being written. Stores nothing. */
export const previewArgusRule = (document: ArgusTreeDocument): Promise<ArgusPreview> =>
  api<ArgusPreview>('/argus/preview', { method: 'POST', body: JSON.stringify({ document }) });

/** A derived-signal formula read into a rule-tree node, for a custom signal.
 *  `conditions` are named conditions it may name, as 1 or 0. A 422's detail says, in words, why the formula cannot be a rule's signal. */
export const argusFormula = (
  formula: string,
  inputs: ArgusTreeDocument['inputs'],
  signals: Record<string, string> = {},
  conditions: Record<string, string> = {},
): Promise<{ expr: ArgusTreeNode; inputs: ArgusTreeDocument['inputs'] }> =>
  api('/argus/formula', { method: 'POST', body: JSON.stringify({ formula, inputs, signals, conditions }) });

export const getArgusFindings = (ruleId: string): Promise<ArgusFindings> =>
  api<ArgusFindings>(`/argus/rules/${encodeURIComponent(ruleId)}/findings`);

/** Open episodes and recent history, optionally for one rule. */
export const getArgusInbox = (ruleId?: string | null): Promise<ArgusInbox> =>
  api<ArgusInbox>(`/argus/inbox${ruleId ? `?rule_id=${encodeURIComponent(ruleId)}` : ''}`);

/** Episodes that are over, newest first, for the History room. */
export const getArgusHistory = (ruleId: string | null, days: number): Promise<ArgusHistory> =>
  api<ArgusHistory>(
    `/argus/history?days=${days}${ruleId ? `&rule_id=${encodeURIComponent(ruleId)}` : ''}`,
  );

/** The Argus cluster now, for the Health room (admins). */
export const getArgusHealth = (): Promise<ArgusHealth> => api<ArgusHealth>('/argus/health');

/** Done and failed jobs per queue over the last `minutes`, for the Health room's queue cards. */
export const getArgusHealthFinished = (minutes: number): Promise<ArgusHealthFinished> =>
  api<ArgusHealthFinished>(`/argus/health/finished?minutes=${minutes}`);

/** What the Argus cluster did over the last `hours`, for the Health room. */
export const getArgusHealthStats = (hours: number): Promise<ArgusHealthStats> =>
  api<ArgusHealthStats>(`/argus/health/stats?hours=${hours}`);

/** The newest backtest runs, with their totals. */
export const listArgusBacktests = (purpose?: 'rule' | 'quick'): Promise<{ backtests: ArgusBacktestSummary[] }> =>
  api(`/argus/backtests${purpose ? `?purpose=${purpose}` : ''}`);

/** The count beside ARGUS: open episodes nobody has marked. */
export const getArgusAttention = (): Promise<ArgusAttention> =>
  api<ArgusAttention>('/argus/inbox/attention');

/** Seen, dismissed, or take the mark off. The database decides who may. */
export const markArgusEpisode = (
  id: number,
  mark: 'seen' | 'dismissed' | null,
): Promise<{ episode_id: number; mark: string | null }> =>
  api(`/argus/episodes/${id}/mark`, { method: 'POST', body: JSON.stringify({ mark }) });

/** A verdict from the fixed list with a reason and note, or null to take it off. */
export const judgeArgusEpisode = (
  id: number,
  verdict: ArgusVerdictKey | null,
  reason: string | null,
  note: string | null,
): Promise<{ episode_id: number }> =>
  api(`/argus/episodes/${id}/verdict`, { method: 'PUT', body: JSON.stringify({ verdict, reason, note }) });

/** Acknowledge (mark `seen`) several of an alert's episodes at once, or take it off (null). */
export const markArgusEpisodes = (
  ids: number[],
  mark: 'seen' | null,
): Promise<{ marked: number; mark: string | null }> =>
  api('/argus/episodes/mark', { method: 'POST', body: JSON.stringify({ episode_ids: ids, mark }) });

/** One verdict for several of an alert's episodes, or null to reopen them. */
export const judgeArgusEpisodes = (
  ids: number[],
  verdict: ArgusVerdictKey | null,
  reason: string | null,
  note: string | null,
): Promise<{ judged: number }> =>
  api('/argus/episodes/verdict', {
    method: 'PUT',
    body: JSON.stringify({ episode_ids: ids, verdict, reason, note }),
  });

/** Take an episode to look into, or let it go. */
export const takeArgusEpisode = (id: number, take: boolean): Promise<{ episode_id: number }> =>
  api(`/argus/episodes/${id}/owner`, { method: 'PUT', body: JSON.stringify({ take }) });

/** Take several of an alert's episodes at once, or let them go. All or none. */
export const takeArgusEpisodes = (ids: number[], take: boolean): Promise<{ changed: number }> =>
  api('/argus/episodes/owner', { method: 'PUT', body: JSON.stringify({ episode_ids: ids, take }) });

/** Mute several of an alert's episodes at once, or wake them (null). All or none. */
export const snoozeArgusEpisodes = (ids: number[], days: number | null): Promise<{ changed: number }> =>
  api('/argus/episodes/snooze', { method: 'PUT', body: JSON.stringify({ episode_ids: ids, days }) });

/** Keep this episode's rule quiet on its entity for some days, or wake it (null). */
export const snoozeArgusEpisode = (id: number, days: number | null): Promise<{ episode_id: number }> =>
  api(`/argus/episodes/${id}/snooze`, { method: 'PUT', body: JSON.stringify({ days }) });

/** Ask a watcher to backtest a saved rule or an unsaved draft. Answers at once. */
export const requestArgusBacktest = (body: {
  rule_id?: string | null;
  document?: ArgusRuleDocument | ArgusTreeDocument | null;
  days?: number;
  /** A chosen window, as ISO times (UTC). Both or neither; replaces `days`. */
  range_from?: string;
  range_to?: string;
  /** `quick`: a Backtest question - never a rule, never counts for deploying. */
  purpose?: 'rule' | 'quick';
  /** Everything the data has; no window (migration 0046). */
  all_time?: boolean;
  label?: string;
}): Promise<ArgusBacktest> =>
  api<ArgusBacktest>('/argus/backtests', { method: 'POST', body: JSON.stringify(body) });

/** How many backtests wait and run now, across every watcher. */
export const getArgusBacktestQueue = (): Promise<{ queued: number; running: number }> =>
  api('/argus/backtests/queue');

export const getArgusBacktest = (id: string): Promise<ArgusBacktest> =>
  api<ArgusBacktest>(`/argus/backtests/${encodeURIComponent(id)}`);

/** The newest backtest of a rule's current version, if any. */
export const getArgusRuleBacktest = (ruleId: string): Promise<{ backtest: ArgusBacktest | null }> =>
  api(`/argus/rules/${encodeURIComponent(ruleId)}/backtest`);

export const listArgusWatchers = (): Promise<{ watchers: ArgusWatcherInfo[]; quiet_after_seconds: number }> =>
  api('/argus/watchers');

export const listArgusRules = (): Promise<{ rules: ArgusRule[] }> =>
  api<{ rules: ArgusRule[] }>('/argus/rules');

export const createArgusRule = (body: {
  name: string;
  document: ArgusRuleDocument | ArgusTreeDocument;
  description?: string | undefined;
  /** The team it belongs to, and its folder (decision 35). */
  group_id?: string | undefined;
  folder_id?: string | undefined;
}): Promise<ArgusRule> =>
  api<ArgusRule>('/argus/rules', { method: 'POST', body: JSON.stringify(body) });

/** Changing a rule writes a new version; nothing is rewritten. */
export const updateArgusRule = (
  id: string,
  body: { name: string; document: ArgusRuleDocument | ArgusTreeDocument; description?: string | undefined },
): Promise<ArgusRule> =>
  api<ArgusRule>(`/argus/rules/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

/** Withdraw a rule. Soft - its versions are somebody's history. */
/** Deploy or retire. The database decides whether this account may. */
export const setArgusRuleStatus = (
  id: string,
  status: 'draft' | 'deployed' | 'retired',
): Promise<ArgusRule> =>
  api<ArgusRule>(`/argus/rules/${encodeURIComponent(id)}/status`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });

/** One episode for the triage page. */
export const getArgusEpisode = (id: number): Promise<ArgusEpisodeDetail> =>
  api<ArgusEpisodeDetail>(`/argus/episodes/${encodeURIComponent(String(id))}`);

/** When a rule runs and how much it reads. Not a new version; on a deployed rule, the leader's call. */
export const setArgusRuleSchedule = (id: string, schedule: ArgusSchedule): Promise<ArgusRule> =>
  api<ArgusRule>(`/argus/rules/${encodeURIComponent(id)}/schedule`, {
    method: 'PUT',
    body: JSON.stringify(schedule),
  });

/** The charts a rule's alerts and backtests show. Not a new version; allowed while it runs. */
export const setArgusRuleCharts = (id: string, charts: ArgusRuleChart[]): Promise<ArgusRule> =>
  api<ArgusRule>(`/argus/rules/${encodeURIComponent(id)}/charts`, {
    method: 'PUT',
    body: JSON.stringify({ charts }),
  });

/** The marks a chart may draw for this viewer: deployed rules, and own drafts. */
export const listArgusOverlays = (): Promise<{ marks: ArgusMark[] }> =>
  api<{ marks: ArgusMark[] }>('/argus/overlays');

export const deleteArgusRule = (id: string): Promise<void> =>
  api<void>(`/argus/rules/${encodeURIComponent(id)}`, { method: 'DELETE' });

/* Teams, folders, Public and follows (decision 35). */

export const listArgusTeams = (): Promise<ArgusTeams> => api<ArgusTeams>('/argus/teams');

export const createArgusFolder = (teamId: string, name: string): Promise<ArgusFolder> =>
  api<ArgusFolder>(`/argus/teams/${encodeURIComponent(teamId)}/folders`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });

export const renameArgusFolder = (id: string, name: string): Promise<ArgusFolder> =>
  api<ArgusFolder>(`/argus/folders/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify({ name }) });

/** Its rules stay, at the top of their team. */
export const deleteArgusFolder = (id: string): Promise<void> =>
  api<void>(`/argus/folders/${encodeURIComponent(id)}`, { method: 'DELETE' });

export const setArgusRuleFolder = (id: string, folderId: string | null): Promise<ArgusRule> =>
  api<ArgusRule>(`/argus/rules/${encodeURIComponent(id)}/folder`, {
    method: 'PUT',
    body: JSON.stringify({ folder_id: folderId }),
  });

/** Team only, or Public. A team leader's call; the database decides. */
export const setArgusRuleVisibility = (id: string, visibility: 'team' | 'public'): Promise<ArgusRule> =>
  api<ArgusRule>(`/argus/rules/${encodeURIComponent(id)}/visibility`, {
    method: 'PUT',
    body: JSON.stringify({ visibility }),
  });

export const followArgusRule = (id: string, follow: boolean): Promise<ArgusRule> =>
  api<ArgusRule>(`/argus/rules/${encodeURIComponent(id)}/follow`, { method: follow ? 'PUT' : 'DELETE' });

/** Add a note to an alert. Answers with every note on it. */
export const addArgusNote = (episodeId: number, body: string): Promise<{ notes: ArgusNote[] }> =>
  api<{ notes: ArgusNote[] }>(`/argus/episodes/${encodeURIComponent(String(episodeId))}/notes`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
